import { useCallback, useState } from 'react';
import { PhoneForwarded } from 'lucide-react';
import { updateInternalCallsFeature } from '../../api';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { canManageInternalCalls, internalCallsToast } from './internalCallsSetting';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/**
 * Анализировать ли внутренние звонки установки на Asterisk. Выключенный тумблер не теряет
 * звонки: запись сохраняется, задание создаётся сразу «Пропущенным» и не тратит минуты.
 */
const InternalCallsCard = ({ user }) => {
  const { toast } = useToast();
  const { features, isLoadingFeatures, refreshFeatures } = useFeatures();
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = useCallback(
    async (analyze) => {
      setIsSaving(true);
      try {
        await updateInternalCallsFeature(analyze);
        await refreshFeatures();
        toast({ title: internalCallsToast(analyze) });
      } catch (error) {
        toast({
          title: 'Не удалось изменить настройку',
          description: describeError(error),
          variant: 'destructive'
        });
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFeatures, toast]
  );

  if (!canManageInternalCalls(user, features)) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <PhoneForwarded className="h-5 w-5" />
            Внутренние звонки
            <Badge variant={features.internalCallsAnalyzed ? 'default' : 'outline'}>
              {features.internalCallsAnalyzed ? 'Анализируются' : 'Не анализируются'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Звонки между внутренними номерами АТС и на короткие номера соседней АТС. Настройка
            есть только у телефонии Asterisk.
          </CardDescription>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="internal-calls-analysis"
            data-ev="settings.internal_calls.analysis_toggle"
            checked={features.internalCallsAnalyzed}
            disabled={isSaving || isLoadingFeatures}
            onCheckedChange={handleToggle}
          />
          <Label htmlFor="internal-calls-analysis">Анализировать</Label>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 text-sm text-muted-foreground">
        <p>
          При выключении внутренние звонки по-прежнему принимаются, а их записи сохраняются. Но
          распознавание речи и анализ для них не запускаются: задание сразу получает статус
          «Пропущено» с причиной «внутренний звонок», и минуты на него не тратятся.
        </p>
        <p>
          Настройка действует на новые звонки. Пропущенные раньше можно разобрать кнопкой
          «Переобработать» на вкладке «Задания» с фильтром «Пропущенные».
        </p>
      </CardContent>
    </Card>
  );
};

export default InternalCallsCard;
